import { ChatWidget } from '@apps/chat-widget';
import { api } from '@packages/backend/convex/_generated/api';
import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
  useParams,
  useRouter,
} from '@tanstack/react-router';
import { signOut } from 'aws-amplify/auth';
import { useQuery } from 'convex-helpers/react';
import { CircleUserIcon, StoreIcon } from 'lucide-react';
import { FeatureEnabled } from '@/components/FeatureEnabled';
import { RestaurantInfoPopover } from '@/components/RestaurantInfoPopover';
import { SidebarWrapper } from '@/components/SidebarWrapper';
import { SuperAdminOnly } from '@/components/SuperAdminOnly';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { ActionButton } from '@/components/ui+/ActionButton';
import { ModeToggle } from '@/components/ui+/ModeToggle';
import { links } from '@/routes/restaurant/links';

export const Route = createFileRoute('/r')({
  beforeLoad: ({ context }) => {
    // Keep the current URL for signed-out users so they can log in and land on the page they asked for.
    if (!context.isAuthenticated) {
      return;
    }
  },
  component: Component,
});

function Component() {
  // `strict: false` allows accessing params from child routes (e.g., $restaurantId), not just this layout route
  const { restaurantId } = useParams({ strict: false });
  const restaurantQuery = useQuery(
    api.restaurants.getById,
    restaurantId ? { restaurantId } : 'skip',
  );

  const router = useRouter();
  const { pathname } = useLocation();

  const handleSignOut = async () => {
    await signOut();
    await router.invalidate();
  };

  return (
    <SidebarWrapper>
      <div className="flex min-h-screen w-full flex-col">
        <header className="sticky top-0 flex h-16 items-center gap-4 border-b bg-background px-4 md:px-6 z-50">
          {/* Mobile sidebar trigger */}
          <SidebarTrigger className="shrink-0 md:hidden" />

          {/* Desktop horizontal navigation */}
          <nav className="hidden flex-col gap-6 text-lg font-medium md:flex md:flex-row md:items-center md:gap-5 md:text-sm lg:gap-6">
            <Link className="flex items-center gap-2 text-lg font-semibold md:text-base" to={'/'}>
              <StoreIcon className="h-6 w-6" />
              <span className="sr-only">Home</span>
            </Link>

            {pathname !== '/r' &&
              links.map((navLink) => {
                return (
                  <Link
                    activeProps={{
                      className: 'text-foreground',
                    }}
                    className="transition-colors hover:text-foreground whitespace-nowrap overflow-hidden text-ellipsis"
                    inactiveProps={{ className: 'text-muted-foreground' }}
                    key={navLink.name}
                    to={navLink.link.to}
                  >
                    {navLink.name}
                  </Link>
                );
              })}
          </nav>

          <div className="flex w-full items-center gap-4 md:ml-auto md:gap-2 lg:gap-4">
            <div className="mr-auto flex-1" />
            <SuperAdminOnly>
              {restaurantQuery.data && <RestaurantInfoPopover restaurant={restaurantQuery.data} />}
            </SuperAdminOnly>
            <ModeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <ActionButton className="rounded-full" size="icon" variant="secondary">
                  <CircleUserIcon className="h-5 w-5" />
                  <span className="sr-only">Toggle user menu</span>
                </ActionButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <SuperAdminOnly>
                  <Link className="text-orange-500 dark:text-orange-400" to={'/a'}>
                    <DropdownMenuItem className={'text-orange-500 dark:text-orange-400'}>
                      Admin view
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </Link>
                </SuperAdminOnly>
                <DropdownMenuItem>Settings</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}>Logout</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <Outlet />
        <FeatureEnabled flag="in-app-chat">
          <ChatWidget kind="support" />
        </FeatureEnabled>
      </div>
    </SidebarWrapper>
  );
}
